import type { User } from "@prisma/client";
import { defineStore } from "pinia";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "~/server/trpc/root";
import superjson from "superjson";

export const useAuthStore = defineStore("auth", () => {
	const user = ref<Partial<User> | null>(null);
	const userAccountObj = ref<Partial<UserAccountClass> | null>(null);
	const pin = ref<string | null>(null);
	const pin_last_verified = ref<Date | null>(null);
	const coins = ref<number>(0);
	const activeSub =
		ref<
			inferRouterOutputs<AppRouter>["rSubscription"]["currentSubscription"]
		>(null);

	function setUser(_user: Partial<User> | null): void {
		user.value = _user;
	}

	function setUserAccount(
		_userAccount: Partial<UserAccountClass> | null,
	): void {
		userAccountObj.value = _userAccount;
	}

	function setPin(_pin: string | null): void {
		pin.value = _pin;
	}
	function hasPin() {
		return pin.value !== null;
	}

	function logout() {
		user.value = null;
		userAccountObj.value = null;
		pin_last_verified.value = null;
		localStorage.removeItem("pin_last_verified");
	}

	function verifyPin(_pin: string): boolean {
		if (`${_pin}` === `${pin.value}`) {
			localStorage.setItem(
				"pin_last_verified",
				superjson.stringify(new Date()),
			);
			pin_last_verified.value = new Date();
			return true;
		} else {
			return false;
		}
	}

	function lockPin(): void {
		pin_last_verified.value = null;
		localStorage.removeItem("pin_last_verified");
	}

	function pinUnlocked(): boolean {
		const v: Date | null =
			pin_last_verified.value !== null
				? pin_last_verified.value
				: localStorage.getItem("pin_last_verified") !== null
					? (superjson.deserialize(
							JSON.parse(
								localStorage.getItem("pin_last_verified")!,
							),
						) as unknown as Date)
					: null;
		return (
			!pin.value ||
			(!!pin.value &&
				!!v &&
				Date.now() - v.getTime() < 30 * 24 * 60 * 60 * 1000)
		);
	}

	return {
		user,
		userAccount: userAccountObj,
		setUser,
		pin_last_verified, // IMPORTANT READ ONLY!!
		setPin,
		hasPin,
		setUserAccount,
		logout,
		verifyPin,
		lockPin,
		pinUnlocked,
		coins,
		activeSub,
	};
});

export const useTvLoginStore = defineStore("tvLogin", () => {
	const login = ref<Partial<LoginRegisterState> | null>(null);

	const temp = localStorage.getItem("tvLogin_login");
	if (temp !== null) {
		login.value = JSON.parse(temp);
	}

	function setLogin(_login: Partial<LoginRegisterState> | null): void {
		login.value = _login;
		localStorage.setItem("tvLogin_login", JSON.stringify(_login));
	}

	return { login, setLogin };
});

export const useLoginRegisterStore = defineStore("loginRegister", () => {
	const login = ref<Partial<LoginRegisterState> | null>(null);

	const temp = localStorage.getItem("loginRegister_login");
	if (temp !== null) {
		login.value = JSON.parse(temp);
	}

	function setLogin(_login: Partial<LoginRegisterState> | null): void {
		login.value = _login;
		localStorage.setItem("loginRegister_login", JSON.stringify(_login));
	}

	return { login, setLogin };
});

export const enum LoginStatus {
	LOGIN_FAIL = 0,
	LOGIN_SUCCESS = 1,
	LOGIN_PASSWORD_FAIL = 2,
	LOGIN_PASSWORD_SUCCESS = 3,
	OTP_EDIT_PHONE_NUMBER = 4,
	OTP_REGISTER_SUCCESS = 5,
	OTP_LOGIN_SUCCESS = 6,
	PASSWORD_LOGIN_SUCCESS = 7,
	REGISTER_BACK = 8,
	REGISTER_SUCCESS = 9,
	TV_LOGIN_SUCCESS = 10,
	TV_LOGIN_FAIL = 11,
	TV_LOGIN_PASSWORD = 12,
	TV_PASSWORD_EDIT_NUMBER = 13,
	TV_OTP_EDIT_NUMBER = 14,
	TV_OTP_SUCCESS = 15,
	TV_OTP_FAIL = 16,
}

type LoginRegisterState = {
	data: Partial<User> | any | null;
	state: LoginStatus;
};

type UserAccountClass = {
	id: number | bigint;
	is_default_account: boolean;
	user_id: number;
	name: string;
	surname: string;
	birth_date: string;
	age_group: number;
	gender: boolean;
	color: string;
};
type PartialUserAccount = Partial<UserAccountClass>;

export type { LoginRegisterState, UserAccountClass, PartialUserAccount };

// Hot Module Replacement
if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
	import.meta.hot.accept(
		acceptHMRUpdate(useLoginRegisterStore, import.meta.hot),
	);
}
